/**
 * API wrapper for app bootstrap data such as portal, business profiles, and app info.
 * This stays thin: it fetches backend data but does not own editor state.
 * Portal loading uses raw axios because the shared API client depends on that data.
 */
import BaseService from './baseService';
import { getPortalDomain } from '@/helpers/portalDomain';
import axios from 'axios';
import type { Portal, PortalResponse } from '@/types/account/portal';
import type { BusinessProfile, BpSetting } from '@/types/account/user';
import { dxApiEndpoints } from '@/constants/dxApiEndpoints';

class AppService extends BaseService {
  // Uses raw axios because apiClient needs portal data before it can initialize.
  fetchPortalData = async (): Promise<Portal> => {
    const portalDomain = getPortalDomain();
    const { data } = await axios.get<PortalResponse>(
      `${import.meta.env.VITE_API_BASE_URL}portals?domain=${portalDomain}`,
      { headers: { 'X-Bypass-Login': 'true' } }
    );
    return data.data[0];
  };

  /** Fetches business profiles available to the current portal user. */
  fetchBusinessProfiles = async (
    portalId: number,
    userId: number
  ): Promise<BusinessProfile[]> => {
    const response = await this.apiClient.get<{ data: BusinessProfile[] }>(
      `portals/${portalId}/users/${userId}/businessProfiles`,
      { userId }
    );
    return response.data;
  };

  /**
   * Fetches the business profile's Settings rows (feature flags live here).
   * `includeInternal` is required — the flags are internal-type settings.
   */
  fetchBpSettings = async (
    businessProfileId: number,
    keyprefixes: string
  ): Promise<BpSetting[]> => {
    const response = await this.apiClient.get<{ data: BpSetting[] }>(
      dxApiEndpoints.GetBusinessProfileSettings(businessProfileId),
      { keyprefixes, includeInternal: true }
    );

    return response.data;
  };
}

export default new AppService();
